import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Edit,
  X,
  CheckCircle2,
  Clock,
  Users,
  Bell,
  ArrowRight,
  AlertCircle,
  FileText,
  Shield,
  Zap,
  MessageSquare,
} from 'lucide-react';
import type { DisputeType, WorkflowStage, WorkflowTransition } from '@/types/dispute-types';

interface WorkflowViewerProps {
  disputeType: DisputeType;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
}

export function WorkflowViewer({ disputeType, open, onClose, onEdit }: WorkflowViewerProps) {
  const { workflow } = disputeType;

  const getStageById = (stageId: string): WorkflowStage | undefined => {
    return workflow.stages.find(s => s.id === stageId);
  };

  const getTransitionsFromStage = (stageId: string): WorkflowTransition[] => {
    return workflow.transitions.filter(t => t.fromStageId === stageId);
  };

  const getTransitionsToStage = (stageId: string): WorkflowTransition[] => {
    return workflow.transitions.filter(t => t.toStageId === stageId);
  };

  const renderStageCard = (stage: WorkflowStage) => {
        const outgoingTransitions = getTransitionsFromStage(stage.id);
        // incomingTransitions available for future use (e.g., showing incoming arrows)
        void getTransitionsToStage(stage.id);

    return (
      <Card key={stage.id} className="border-l-4" style={{ borderLeftColor: stage.color }}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline">Stage {stage.order}</Badge>
                <CardTitle className="text-lg">{stage.name}</CardTitle>
              </div>
              <p className="text-sm text-muted-foreground">{stage.description}</p>
            </div>
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${stage.color}20` }}
            >
              <CheckCircle2 className="h-6 w-6" style={{ color: stage.color }} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stage Properties */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Stage Properties
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {stage.properties.requiresApproval && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CheckCircle2 className="h-3 w-3" />
                  <span>Requires Approval</span>
                </div>
              )}
              {stage.properties.slaHours && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>SLA: {stage.properties.slaHours}h</span>
                </div>
              )}
              {stage.properties.autoEscalate && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Zap className="h-3 w-3" />
                  <span>Auto-escalate</span>
                </div>
              )}
              {stage.properties.allowReassignment && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="h-3 w-3" />
                  <span>Allow Reassignment</span>
                </div>
              )}
            </div>
          </div>

          {/* Approver Roles */}
          {stage.properties.requiresApproval && stage.properties.approverRoles && stage.properties.approverRoles.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />
                Approver Roles
              </h4>
              <div className="flex flex-wrap gap-1">
                {stage.properties.approverRoles.map((role, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {role}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Escalation Roles */}
          {stage.properties.autoEscalate && stage.properties.escalationRoles && stage.properties.escalationRoles.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Escalation Roles
              </h4>
              <div className="flex flex-wrap gap-1">
                {stage.properties.escalationRoles.map((role, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {role}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Allowed Assignee Roles */}
          {stage.properties.allowReassignment && stage.properties.allowedAssigneeRoles && stage.properties.allowedAssigneeRoles.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />
                Allowed Assignee Roles
              </h4>
              <div className="flex flex-wrap gap-1">
                {stage.properties.allowedAssigneeRoles.map((role, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {role}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Notifications */}
          {stage.notifications && stage.notifications.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Notifications ({stage.notifications.length})
              </h4>
              <div className="space-y-1">
                {stage.notifications.map((notif, idx) => (
                  <div key={idx} className="text-xs text-muted-foreground flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {notif.event.replace(/_/g, ' ')}
                    </Badge>
                    <span>→</span>
                    <span>{notif.channels.join(', ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Outgoing Transitions */}
          {outgoingTransitions.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <ArrowRight className="h-4 w-4" />
                Available Transitions
              </h4>
              <div className="space-y-1">
                {outgoingTransitions.map((transition) => {
                  const toStage = getStageById(transition.toStageId);
                  return (
                    <div
                      key={transition.id}
                      className="text-xs p-2 border rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">{transition.name}</span>
                        <span className="text-muted-foreground">→</span>
                        <Badge
                          variant="outline"
                          style={{
                            backgroundColor: toStage ? `${toStage.color}20` : undefined,
                            borderColor: toStage?.color,
                          }}
                        >
                          {toStage?.name || 'Unknown'}
                        </Badge>
                      </div>
                      <div className="flex gap-1">
                        {transition.requiresComment && (
                          <Badge variant="secondary" className="text-xs">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Comment
                          </Badge>
                        )}
                        {transition.requiresApproval && (
                          <Badge variant="secondary" className="text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            Approval
                          </Badge>
                        )}
                        {transition.autoTransition && (
                          <Badge variant="secondary" className="text-xs">
                            <Zap className="h-3 w-3 mr-1" />
                            Auto
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {disputeType.name} - Workflow
          </DialogTitle>
          <DialogDescription>{disputeType.description}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4">
            {/* Workflow Summary */}
            <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
              <CardContent className="pt-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground mb-1">Total Stages</p>
                    <p className="text-2xl font-bold">{workflow.stages.length}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Transitions</p>
                    <p className="text-2xl font-bold">{workflow.transitions.length}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Status</p>
                    <Badge className={disputeType.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : ''}>
                      {disputeType.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Separator />

            {/* Workflow Stages */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Workflow Stages</h3>
              <div className="space-y-4">
                {workflow.stages
                  .sort((a, b) => a.order - b.order)
                  .map((stage) => renderStageCard(stage))}
              </div>
            </div>

            {/* Workflow Transitions Summary */}
            {workflow.transitions.length > 0 && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">All Transitions</h3>
                  <div className="grid gap-3">
                    {workflow.transitions.map((transition) => {
                      const fromStage = getStageById(transition.fromStageId);
                      const toStage = getStageById(transition.toStageId);

                      return (
                        <Card key={transition.id}>
                          <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Badge
                                  variant="outline"
                                  style={{
                                    backgroundColor: fromStage ? `${fromStage.color}20` : undefined,
                                    borderColor: fromStage?.color,
                                  }}
                                >
                                  {fromStage?.name || 'Unknown'}
                                </Badge>
                                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                <Badge
                                  variant="outline"
                                  style={{
                                    backgroundColor: toStage ? `${toStage.color}20` : undefined,
                                    borderColor: toStage?.color,
                                  }}
                                >
                                  {toStage?.name || 'Unknown'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{transition.name}</span>
                                <div className="flex gap-1">
                                  {transition.requiresComment && (
                                    <Badge variant="secondary" className="text-xs">
                                      Comment Required
                                    </Badge>
                                  )}
                                  {transition.requiresApproval && (
                                    <Badge variant="secondary" className="text-xs">
                                      Approval Required
                                    </Badge>
                                  )}
                                  {transition.autoTransition && (
                                    <Badge variant="secondary" className="text-xs">
                                      Auto ({transition.autoTransitionDelay}h)
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            {transition.conditions && transition.conditions.length > 0 && (
                              <div className="mt-2 pt-2 border-t">
                                <p className="text-xs text-muted-foreground mb-1">Conditions:</p>
                                <div className="flex flex-wrap gap-1">
                                  {transition.conditions.map((cond, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {cond.field} {cond.operator} {cond.value}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Usage Statistics */}
            {disputeType.usageCount > 0 && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Usage Statistics</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground mb-1">Total Disputes</p>
                        <p className="text-2xl font-bold">{disputeType.usageCount}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground mb-1">Avg Resolution Time</p>
                        <p className="text-2xl font-bold">{disputeType.avgResolutionTime} days</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground mb-1">Success Rate</p>
                        <p className="text-2xl font-bold">{disputeType.successRate}%</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
          <Button onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
